import React, { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  removeBasket,
  selectBasketList,
} from "../../react-reduxs/slices/baskets/basker-slice";
import { AppDispatch } from "../../react-reduxs/configure_store";
import BasketOrdersSendComponent from "./components/BasketOrdersSendComponent";
import BasketEditButtonComponent from "./components/BasketEditButtonComponent";
import { ApiUrl } from "../../consts/api";

interface BasketComponentProps {
  color?: string;
}

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* ── Tasarım sabitleri ──────────────────────────────────────────────────────── */
const GREEN = "#4CAF87"; // ana yeşil (başlık, vurgu)
const GREEN_LIGHT = "#EBF7F2"; // arka plan hafif yeşil
const BRAND_LIGHT = "rgba(84,158,131,0.08)";
const GRAY_TEXT = "#8E9BAB"; // gri metin
const DARK_TEXT = "#1A2332"; // koyu metin
const BORDER = "#ECF0F4"; // ayraç

const BasketComponent: React.FC<BasketComponentProps> = ({ color = GREEN }) => {
  const dispatch: AppDispatch = useDispatch();
  const basketList = useSelector(selectBasketList);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const basketTotal = useCallback(
    () => basketList.reduce((sum, item) => sum + item.price * item.amount, 0),
    [basketList],
  );

  const totalItems = basketList.reduce((s, x) => s + x.amount, 0);

  return (
    <div style={{ position: "relative", fontFamily: "'Nunito', sans-serif" }}>
      {/* ── Tetikleyici buton ─────────────────────────────────────────────── */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          width: 50,
          height: 50,
          background: "#fff",
          border: `1.5px solid transparent`,
          borderRadius: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: open ? "scale(0.94)" : "scale(1)",
          cursor: "pointer",
          padding: 0,
          boxShadow: "0 2px 6px rgba(84, 158, 131, 0.2)",
          transition: "box-shadow .15s",
        }}
        aria-label="Sepeti aç"
      >
        <i
          style={{ color, fontSize: 25 }}
          className="fa-duotone fa-cart-plus"
        />
        {totalItems > 0 && (
          <span
            style={{
              position: "absolute",
              top: -7,
              right: -7,
              minWidth: 20,
              height: 20,
              background: GREEN,
              borderRadius: 10,
              color: "#fff",
              fontSize: 10,
              fontWeight: 900,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 5px",
              border: "2px solid #fff",
              letterSpacing: 0,
            }}
          >
            {totalItems}
          </span>
        )}
      </button>

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      {open && (
        <>
          {/* overlay */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 90 }}
          />

          <div
            ref={panelRef}
            style={{
              position: "absolute",
              bottom: "calc(100% + 10px)",
              right: 0,
              width: 340,
              maxWidth: "calc(100vw - 24px)",
              background: "#fff",
              borderRadius: 18,
              border: `1px solid ${BORDER}`,
              boxShadow: "0 -6px 40px rgba(0,0,0,0.10)",
              zIndex: 100,
              overflow: "hidden",
            }}
          >
            {/* ── Başlık ───────────────────────────────────────────────────── */}
            <div
              style={{
                padding: "10px 10px 10px",
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                backgroundColor: BRAND_LIGHT,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    color: GREEN,

                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                  }}
                >
                  Sepet Detayı
                </span>
                {basketList.length > 0 && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      color: GRAY_TEXT,
                    }}
                  >
                    {totalItems} ürün
                  </span>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{
                  width: 26,
                  height: 26,
                  background: GREEN_LIGHT,
                  border: "none",
                  borderRadius: 8,
                  color: GREEN,
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* ── Ürün listesi ─────────────────────────────────────────────── */}
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {basketList.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 16px",
                    color: GRAY_TEXT,
                  }}
                >
                  <div style={{ fontSize: 38, marginBottom: 10 }}>🛒</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    Sepetiniz boş
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4 }}>
                    Menüden ürün ekleyin
                  </div>
                </div>
              ) : (
                basketList.map((x, idx) => (
                  <div
                    key={x.menuProductId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 10px",
                      borderBottom:
                        idx < basketList.length - 1
                          ? `1px solid ${BORDER}`
                          : "none",
                    }}
                  >
                    {/* Fotoğraf */}
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 10,
                        overflow: "hidden",
                        flexShrink: 0,
                        background: GREEN_LIGHT,
                      }}
                    >
                      <img
                        src={`${ApiUrl}/${x.menuProductPhoto}`}
                        alt={x.menuProductName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/restaurant.png";
                        }}
                      />
                    </div>

                    {/* Ad + fiyat + not */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: DARK_TEXT,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {x.menuProductName}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: DARK_TEXT,
                          marginTop: 2,
                        }}
                      >
                        {fmt(x.price)} ₺
                      </div>
                      {x.orderNote && (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            marginTop: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            color: GRAY_TEXT,
                            background: "#F5F6F8",
                            padding: "2px 7px",
                            borderRadius: 6,
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          📝 {x.orderNote}
                        </div>
                      )}
                    </div>

                    {/* Adet pill + aksiyonlar */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      {/* ×N pill */}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: DARK_TEXT,
                          background: "#F0F2F5",
                          borderRadius: 8,
                          padding: "3px 10px",
                          letterSpacing: 0.2,
                        }}
                      >
                        ×{x.amount}
                      </span>

                      {/* Düzenle + sil */}
                      <div style={{ display: "flex", gap: 4 }}>
                        <BasketEditButtonComponent basketModel={x} />
                        <button
                          onClick={() =>
                            dispatch(removeBasket({ id: x.menuProductId }))
                          }
                          style={{
                            width: 26,
                            height: 26,
                            background: "#FEF2F2",
                            border: "none",
                            borderRadius: 7,
                            color: "#EF4444",
                            fontSize: 13,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                          title="Kaldır"
                        >
                          <i className="fa-duotone fa-circle-xmark" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* ── Toplam + sipariş ─────────────────────────────────────────── */}
            {basketList.length > 0 && (
              <div
                style={{
                  borderTop: `1.5px solid ${BORDER}`,
                  padding: "8px 8px",
                  background: BRAND_LIGHT,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      color: GRAY_TEXT,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                    }}
                  >
                    Toplam Tutar
                  </span>
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 900,
                      color: GREEN,
                      letterSpacing: 0.3,
                    }}
                  >
                    {fmt(basketTotal())} ₺
                  </span>
                </div>

                <BasketOrdersSendComponent basketList={basketList} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BasketComponent;
